import type { Action, MiniAction } from '@otto-ec/tracking-bct';
import type { BrowserPageSnapshot } from '@shared/browser/BrowserPage.browser.ts';
import { log } from '@shared/log.browser.ts';
import type { KnownTrackingLabels, TrackingLabels } from '@shared/tracking/TrackingLabels';
import { type OnColorChangePayload, isSimilarProduct } from '@shared/types/types';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { ProductItem, TileItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import {
    featureIdFromLocalListPosition,
    featuresFromTileListItems,
} from '@tilelist/domain/tracking/FeatureTracking.ts';
import { getSelectionRulesTracking } from '@tilelist/services/tracking/SelectionRuleTracking.browser.ts';

type ColorChangeTrackingParams = OnColorChangePayload & { localListPosition: number | undefined };

export type TrackingGateway = {
    submitEvent(labels: KnownTrackingLabels): void;
    addToPageImpression(labels: TrackingLabels, features?: import('@otto-ec/tracking-bct').Feature[]): void;
    trackOnNextPageImpression(labels: TrackingLabels): void;
    submitAction(labels: KnownTrackingLabels, action: Action): void;
    submitMiniActions(miniActions: MiniAction[]): void;
    getPageMergeId(): Promise<string>;
};

export class TrackingService implements TrackingPort {
    constructor(
        private readonly tracking: TrackingGateway,
        private readonly readBrowserPage: () => BrowserPageSnapshot,
    ) {}

    async getPageViewTrackingSfid(): Promise<string> {
        return this.tracking.getPageMergeId();
    }

    trackTileVisibility(item: TileItem): void {
        const featureId = featureIdFromLocalListPosition(item.localListPosition);
        const miniAction: MiniAction = { featureId, action: 'scroll', status: 'visible' };

        log.debug(`trackTileVisibility -- miniAction ${JSON.stringify(miniAction)}`);
        this.tracking.submitMiniActions([miniAction]);
    }

    trackColorChange(params: ColorChangeTrackingParams): void {
        const { variationId, amountColors, localListPosition } = params;

        if (amountColors && localListPosition) {
            const colorChangeTrackingData: Pick<TrackingLabels, 'san_Interaction'> = {
                san_Interaction: 'product_color_change',
            };
            const colorChangeFeatureTracking: Action = {
                name: 'change',
                features: [
                    {
                        id: 'find_tile_' + localListPosition,
                        name: 'tile',
                        status: 'changed',
                        position: +localListPosition,
                        variationId: variationId,
                        labels: {
                            san_ProductColorCount: [`${+amountColors}`],
                        },
                    },
                ],
            };
            log.debug(
                `trackColorChange ${JSON.stringify(colorChangeTrackingData)} -- action ${JSON.stringify(colorChangeFeatureTracking)}`,
            );
            this.tracking.submitAction(colorChangeTrackingData, colorChangeFeatureTracking);
        }
    }

    trackClick(event: Event, productItem: ProductItem, rule: string, entryRule: string): void {
        const { currentTarget } = event;
        if (!(currentTarget instanceof HTMLElement)) return;

        const element = currentTarget;
        const linkType = element.dataset.linkType;
        const { pageCluster } = this.readBrowserPage();
        if (!linkType || !pageCluster) return;

        const trackingData: TrackingLabels = productItem.product.clickTracking
            ? {
                  ...productItem.product.clickTracking,
                  ...getSelectionRulesTracking(rule, entryRule),
              }
            : getSelectionRulesTracking(rule, entryRule);

        if (isSimilarProduct(productItem.product)) {
            trackingData['wk.san_ListPositionSimilar'] = productItem.originPosition + 1;
        }

        trackingData.san_Interaction = `${productItem.product.productType}_product_tile`;
        trackingData['wk.san_ListPosition'] = productItem.actualListPosition;
        trackingData['wk.san_ProductType'] = productItem.product.productType;
        trackingData['wk.san_ProductClickListType'] = pageCluster;
        trackingData['wk.san_ProductLinkType'] = linkType;

        log.debug(`trackClick ${JSON.stringify(trackingData)}`);
        this.tracking.trackOnNextPageImpression(trackingData);
    }

    trackBrandTileClick(listPosition: number, rule: string, entryRule: string): void {
        const { pageCluster } = this.readBrowserPage();
        if (!pageCluster) return;

        const trackingData: TrackingLabels = {
            ...getSelectionRulesTracking(rule, entryRule),
            san_Interaction: 'brand_tile',
            san_ListPosition: listPosition,
            san_ProductType: 'brand',
        };

        log.debug(`trackBrandTileClick ${JSON.stringify(trackingData)}`);
        this.tracking.trackOnNextPageImpression(trackingData);
    }

    trackPageView(pageView: TileListPageLayout, additionalTracking: TrackingLabels): void {
        const baseTrackingLabels = {
            ...additionalTracking,
            ...pageView.tracking,
        };

        const features = featuresFromTileListItems(pageView.tileListItems);

        log.debug(`trackOnPageView ${JSON.stringify(baseTrackingLabels)} -- ${JSON.stringify(features)}`);
        this.tracking.addToPageImpression(baseTrackingLabels, features);
    }

    trackPagination() {
        log.debug(`trackPagination -- san_Interaction: paging_bottom`);
        this.tracking.addToPageImpression({ san_Interaction: 'paging_bottom' });
    }

    trackUrl(url: string): void {
        log.debug(`trackUrl ${url}`);
        this.tracking.addToPageImpression({
            ot_VisibleUrl: decodeURI(url),
        });
    }

    trackPageCluster(pageCluster: string) {
        log.debug(`trackPageCluster ${pageCluster}`);
        this.tracking.addToPageImpression({ ot_PageCluster: pageCluster });
    }

    trackComparisonActivity(activity: NonNullable<KnownTrackingLabels['san_ComparisonActivity']>) {
        log.debug(`trackComparisonActivity -- activity: ${activity}`);
        this.tracking.submitEvent({ san_ComparisonActivity: activity });
    }

    trackInteraction(interaction: KnownTrackingLabels['san_Interaction']) {
        log.debug(`trackInteraction -- san_Interaction: ${interaction}`);
        this.tracking.submitEvent({ san_Interaction: interaction });
    }

    trackProductImageChange(imagePosition: number) {
        const flexLabel = `image_${imagePosition}`;
        log.debug(
            `trackProductImageChange -- san_Interaction: product_image_change -- san_ReptileExperimentFlexLabel: ${flexLabel}`,
        );
        this.tracking.submitEvent({
            san_Interaction: 'product_image_change',
            san_ReptileExperimentFlexLabel: flexLabel,
        });
    }

    trackListLayer(layer: NonNullable<KnownTrackingLabels['san_ListLayer']>) {
        log.debug(`trackListLayer -- san_ListLayer: ${layer}`);
        this.tracking.submitEvent({ san_ListLayer: layer });
    }
}
